import {
  DishIdListSchema,
  DishInputListSchema,
  getDatabaseErrorHint,
  MenuReadResultSchema,
  MenuUrlSchema,
  pickReadings,
  PilotCandidateListSchema,
  PilotListSchema,
  PilotSwapReasonSchema,
  PUBLISH_FIELDS,
  TeamRoleSchema,
  ZoneSummaryListSchema,
  type DatabaseErrorHint,
  type DishInput,
  type FactState,
  type MenuFormat,
  type MenuReadItem,
  type MenuReading,
  type MenuStatus,
  type PilotCandidate,
  type PilotRow,
  type PlaceStatus,
  type PlaceUpdate,
  type TeamRole,
  type ZoneSummary,
} from '@antoja/shared';
import type { AntojaClient } from './supabase';

/**
 * Everything the console asks of Supabase, behind one interface so screens can be
 * tested with a fake. Row level security decides what each call may see or change.
 */
export interface ConsoleApi {
  /** Email of the signed-in person, or null. */
  getSessionEmail(): Promise<string | null>;
  /**
   * Calls the listener whenever someone signs in or out. `recovery` is true when the
   * session comes from a "set a new password" link. Returns an unsubscribe function.
   */
  onSessionChange(listener: (email: string | null, recovery: boolean) => void): () => void;
  /** Sends a sign-in link. Only existing accounts receive one (no sign-ups from the console). */
  sendSignInLink(email: string, redirectTo: string): Promise<void>;
  /** Signs in with email and password. Throws AuthFailure. */
  signInWithPassword(email: string, password: string): Promise<void>;
  /** Sends a link to create or reset the password. Same answer for any address. */
  sendPasswordReset(email: string, redirectTo: string): Promise<void>;
  /** Sets the signed-in person's password. Throws AuthFailure. */
  updatePassword(password: string): Promise<void>;
  signOut(): Promise<void>;
  /** The caller's active team role, or null when the account is not on the team. */
  getMyRole(): Promise<TeamRole | null>;
  listPilotZones(): Promise<ZoneSummary[]>;
  listPlaces(query: PlaceQuery): Promise<PlacePage>;
  getPlace(id: string): Promise<PlaceDetail | null>;
  updatePlace(id: string, patch: PlaceUpdate): Promise<void>;
  setPublished(id: string, published: boolean): Promise<void>;
  /** The place's current menus, oldest first. */
  listMenus(placeId: string): Promise<MenuSummary[]>;
  /** Adds a business menu (website or PDF) and returns its id. Platform pages are refused. */
  addMenu(placeId: string, url: string): Promise<string>;
  getMenu(id: string): Promise<MenuDetail | null>;
  /** Stores a first reading typed or pasted by the team (until the automatic one, C2.1). */
  saveFirstReading(menuId: string, items: MenuReadItem[]): Promise<void>;
  /**
   * Saves new and changed dishes (each existing one with the updated_at it was loaded
   * with) and deactivates the removed ids. Returns how many rows changed.
   */
  saveDishes(menuId: string, dishes: DishInput[], removed: string[]): Promise<number>;
  /** The pilot list: selected places and their reserve, with their FSQ link. Any team role. */
  listPilot(): Promise<PilotRow[]>;
  /** Up to `limit` FSQ places of the row's zone that look like it. Any team role. */
  listPilotCandidates(listKey: string, limit?: number): Promise<PilotCandidate[]>;
  /** Editors: link a row to an FSQ place of its zone, undo it, or say it is not in FSQ. */
  linkPilotPlace(listKey: string, placeId: string): Promise<void>;
  unlinkPilotPlace(listKey: string): Promise<void>;
  markPilotNotInFsq(listKey: string): Promise<void>;
  /** Admins: a reserve row of the same zone takes a selected row's place. */
  swapPilotPlace(selectedKey: string, reserveKey: string, reason: string): Promise<void>;
}

export interface MenuSummary {
  readonly id: string;
  readonly url: string | null;
  readonly format: MenuFormat;
  readonly status: MenuStatus;
  readonly isLinkOnly: boolean;
  readonly lastReadAt: string | null;
}

export interface MenuDish {
  readonly id: string;
  readonly section: string | null;
  readonly name: string;
  readonly price: number | null;
  readonly priceSeenOn: string | null;
  readonly updatedAt: string;
}

export interface MenuDetail extends MenuSummary {
  readonly placeId: string;
  readonly placeName: string;
  readonly first: MenuReading | null;
  readonly second: MenuReading | null;
  readonly dishes: readonly MenuDish[];
}

export type PlaceFilter =
  | 'all'
  | 'ready' // not published yet, location and status verified: can be published now
  | 'unverified' // location or status not verified (or expired): cannot be published
  | 'published'
  | 'unpublished'
  | 'hidden';

export interface PlaceQuery {
  readonly search: string;
  readonly filter: PlaceFilter;
  readonly zoneId: number | null;
  readonly page: number;
  readonly pageSize: number;
}

export interface PlaceListItem {
  readonly id: string;
  readonly name: string;
  readonly zoneName: string | null;
  readonly status: PlaceStatus;
  readonly isPublished: boolean;
  readonly hiddenUntil: string | null;
  readonly facts: readonly FactState[];
}

export interface PlacePage {
  readonly items: readonly PlaceListItem[];
  readonly total: number;
}

export interface PlaceDetail extends PlaceListItem {
  readonly address: string | null;
  readonly zoneId: number | null;
  readonly priceLevel: number | null;
  readonly googlePlaceId: string | null;
  readonly fsqId: string | null;
  readonly origin: string;
  readonly updatedAt: string;
  readonly facts: readonly (FactState & {
    readonly verified_at: string | null;
    readonly family_count: number;
  })[];
}

/** An error the database raised on purpose, with its code (ADR 0009: codes, not sentences). */
export class DatabaseRuleError extends Error {
  constructor(readonly hint: DatabaseErrorHint) {
    super(hint);
    this.name = 'DatabaseRuleError';
  }
}

export type AuthFailureReason =
  | 'invalid_credentials'
  | 'rate_limited'
  | 'weak_password'
  | 'same_password'
  | 'reauthentication_needed'
  | 'failed';

/** A sign-in or password problem the screens can explain (no Supabase text reaches them). */
export class AuthFailure extends Error {
  constructor(readonly reason: AuthFailureReason) {
    super(reason);
    this.name = 'AuthFailure';
  }
}

function authFailure(error: {
  code?: string | undefined;
  status?: number | undefined;
}): AuthFailure {
  // An unconfirmed account answers like a wrong password: the form never says who exists.
  if (error.code === 'invalid_credentials' || error.code === 'email_not_confirmed') {
    return new AuthFailure('invalid_credentials');
  }
  if (error.status === 429 || error.code?.startsWith('over_'))
    return new AuthFailure('rate_limited');
  if (error.code === 'weak_password') return new AuthFailure('weak_password');
  if (error.code === 'same_password') return new AuthFailure('same_password');
  // "Secure password change": the last sign-in is too old to change the password.
  if (error.code === 'reauthentication_needed') return new AuthFailure('reauthentication_needed');
  return new AuthFailure('failed');
}

/** Escapes the LIKE wildcards so a search for "50%" means that text. */
export function likePattern(search: string): string {
  return `%${search.trim().replace(/[\\%_]/g, (char) => `\\${char}`)}%`;
}

function fail(error: { message: string; code?: string | undefined }): never {
  const hint = getDatabaseErrorHint(error);
  if (hint) throw new DatabaseRuleError(hint);
  throw new Error(error.message, { cause: error });
}

/** Like fail(), for writes: row level security refusing one (42501) means the role cannot. */
function failWrite(error: { message: string; code?: string | undefined }): never {
  if (!getDatabaseErrorHint(error) && error.code === '42501') {
    throw new DatabaseRuleError('team_only');
  }
  fail(error);
}

const MENU_COLUMNS = 'id, url, format, status, is_link_only, menu_reads(reader, created_at)';

function lastRead(reads: readonly { created_at: string }[]): string | null {
  return (
    reads
      .map((read) => read.created_at)
      .sort()
      .at(-1) ?? null
  );
}

const PLACE_COLUMNS =
  'id, name, status, is_published, hidden_until, zone:zones(name), verifications(field, status, expires_at, verified_at, family_count)';

export function createSupabaseApi(client: AntojaClient): ConsoleApi {
  return {
    async getSessionEmail() {
      const { data, error } = await client.auth.getSession();
      if (error) fail(error);
      return data.session?.user.email ?? null;
    },

    onSessionChange(listener) {
      const { data } = client.auth.onAuthStateChange((event, session) => {
        listener(session?.user.email ?? null, event === 'PASSWORD_RECOVERY');
      });
      return () => data.subscription.unsubscribe();
    },

    async sendSignInLink(email, redirectTo) {
      const { error } = await client.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo, shouldCreateUser: false },
      });
      if (!error) return;
      // Same answer whether the address has an account or not, so the form never
      // reveals who is on the team: "no account" (otp_disabled) and "wait before the
      // next link" (429, only ever seen by existing accounts) both look like success.
      if (error.code === 'otp_disabled' || error.status === 429) return;
      fail(error);
    },

    async signInWithPassword(email, password) {
      const { error } = await client.auth.signInWithPassword({ email, password });
      if (error) throw authFailure(error);
    },

    async sendPasswordReset(email, redirectTo) {
      const { error } = await client.auth.resetPasswordForEmail(email, { redirectTo });
      // Supabase answers the same for unknown addresses; a 429 is hidden the same way
      // as for sign-in links, so the form never tells who is on the team.
      if (!error || error.status === 429) return;
      fail(error);
    },

    async updatePassword(password) {
      const { error } = await client.auth.updateUser({ password });
      if (error) throw authFailure(error);
    },

    async signOut() {
      const { error } = await client.auth.signOut();
      if (error) fail(error);
    },

    async getMyRole() {
      const { data: userData, error: userError } = await client.auth.getUser();
      if (userError) fail(userError);
      const userId = userData.user?.id;
      if (!userId) return null;
      const { data, error } = await client
        .from('team_members')
        .select('role')
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();
      if (error) fail(error);
      return data ? TeamRoleSchema.parse(data.role) : null;
    },

    async listPilotZones() {
      const { data, error } = await client
        .from('zones')
        .select('id, name')
        .eq('is_pilot', true)
        .order('name');
      if (error) fail(error);
      return ZoneSummaryListSchema.parse(data);
    },

    async listPlaces({ search, filter, zoneId, page, pageSize }) {
      let request = client
        .from('places')
        .select(PLACE_COLUMNS, { count: 'exact' })
        .in('verifications.field', [...PUBLISH_FIELDS])
        .is('verifications.menu_id', null)
        .is('verifications.dish_id', null)
        .order('name')
        .range(page * pageSize, page * pageSize + pageSize - 1);
      if (search.trim()) request = request.ilike('name', likePattern(search));
      if (zoneId !== null) request = request.eq('zone_id', zoneId);
      // ready_to_publish is a computed field (migration 20261105000000), team only.
      if (filter === 'ready') {
        request = request.eq('is_published', false).is('ready_to_publish', true);
      }
      if (filter === 'unverified') request = request.is('ready_to_publish', false);
      if (filter === 'published') request = request.eq('is_published', true);
      if (filter === 'unpublished') request = request.eq('is_published', false);
      if (filter === 'hidden') request = request.gt('hidden_until', new Date().toISOString());
      const { data, error, count } = await request;
      if (error) fail(error);
      return {
        total: count ?? data.length,
        items: data.map((row) => ({
          id: row.id,
          name: row.name,
          zoneName: row.zone?.name ?? null,
          status: row.status,
          isPublished: row.is_published,
          hiddenUntil: row.hidden_until,
          facts: row.verifications,
        })),
      };
    },

    async getPlace(id) {
      const { data, error } = await client
        .from('places')
        .select(
          `${PLACE_COLUMNS}, address, zone_id, price_level, google_place_id, fsq_id, origin, updated_at`,
        )
        .eq('id', id)
        .in('verifications.field', [...PUBLISH_FIELDS])
        .is('verifications.menu_id', null)
        .is('verifications.dish_id', null)
        .maybeSingle();
      if (error) fail(error);
      if (!data) return null;
      return {
        id: data.id,
        name: data.name,
        zoneName: data.zone?.name ?? null,
        status: data.status,
        isPublished: data.is_published,
        hiddenUntil: data.hidden_until,
        facts: data.verifications,
        address: data.address,
        zoneId: data.zone_id,
        priceLevel: data.price_level,
        googlePlaceId: data.google_place_id,
        fsqId: data.fsq_id,
        origin: data.origin,
        updatedAt: data.updated_at,
      };
    },

    async updatePlace(id, patch) {
      const { data, error } = await client.from('places').update(patch).eq('id', id).select('id');
      if (error) failWrite(error);
      if (data.length === 0) throw new DatabaseRuleError('team_only'); // RLS hid the row: not an editor
    },

    async setPublished(id, published) {
      const { data, error } = await client
        .from('places')
        .update({ is_published: published })
        .eq('id', id)
        .select('id');
      if (error) failWrite(error);
      if (data.length === 0) throw new DatabaseRuleError('team_only');
    },

    async listMenus(placeId) {
      const { data, error } = await client
        .from('menus')
        .select(MENU_COLUMNS)
        .eq('place_id', placeId)
        .eq('is_current', true)
        .order('created_at');
      if (error) fail(error);
      return data.map((row) => ({
        id: row.id,
        url: row.url,
        format: row.format,
        status: row.status,
        isLinkOnly: row.is_link_only,
        lastReadAt: lastRead(row.menu_reads),
      }));
    },

    async addMenu(placeId, url) {
      // Same rule as the database, answered before the round trip.
      const parsed = MenuUrlSchema.safeParse(url);
      const issue = parsed.error?.issues[0]?.message;
      if (issue === 'menu_url_invalid' || issue === 'menu_url_is_platform') {
        throw new DatabaseRuleError(issue);
      }
      const { data, error } = await client.rpc('add_menu', {
        p_place_id: placeId,
        p_url: parsed.data ?? url,
      });
      if (error) failWrite(error);
      return data;
    },

    async getMenu(id) {
      const { data, error } = await client
        .from('menus')
        .select(
          'id, url, format, status, is_link_only, place_id, place:places(name), menu_reads(reader, result, created_at), dishes(id, section, name, price, price_seen_on, updated_at)',
        )
        .eq('id', id)
        .eq('dishes.is_active', true)
        .order('created_at', { referencedTable: 'dishes' })
        .maybeSingle();
      if (error) fail(error);
      if (!data) return null;
      // One bad reading must not hide the menu: it is shown as unreadable.
      const reads: MenuReading[] = data.menu_reads.map((read) => {
        const items = MenuReadResultSchema.safeParse(read.result);
        return {
          reader: read.reader,
          createdAt: read.created_at,
          readable: items.success,
          items: items.data ?? [],
        };
      });
      return {
        id: data.id,
        url: data.url,
        format: data.format,
        status: data.status,
        isLinkOnly: data.is_link_only,
        lastReadAt: lastRead(data.menu_reads),
        placeId: data.place_id,
        placeName: data.place?.name ?? '',
        ...pickReadings(reads),
        dishes: data.dishes.map((dish) => ({
          id: dish.id,
          section: dish.section,
          name: dish.name,
          price: dish.price,
          priceSeenOn: dish.price_seen_on,
          updatedAt: dish.updated_at,
        })),
      };
    },

    async saveFirstReading(menuId, items) {
      const result = MenuReadResultSchema.parse(items);
      const { error } = await client
        .from('menu_reads')
        .insert({ menu_id: menuId, reader: 'team', result });
      if (error) failWrite(error);
    },

    async saveDishes(menuId, dishes, removed) {
      const { data, error } = await client.rpc('save_menu_dishes', {
        p_menu_id: menuId,
        p_dishes: DishInputListSchema.parse(dishes),
        p_removed: DishIdListSchema.parse(removed),
      });
      if (error) failWrite(error);
      return data;
    },

    async listPilot() {
      const { data, error } = await client.rpc('pilot_list');
      if (error) fail(error);
      return PilotListSchema.parse(data);
    },

    async listPilotCandidates(listKey, limit = 5) {
      const { data, error } = await client.rpc('pilot_link_candidates', {
        p_list_key: listKey,
        p_limit: limit,
      });
      if (error) fail(error);
      return PilotCandidateListSchema.parse(data);
    },

    async linkPilotPlace(listKey, placeId) {
      const { error } = await client.rpc('link_pilot_place', {
        p_list_key: listKey,
        p_place_id: placeId,
      });
      if (error) failWrite(error);
    },

    async unlinkPilotPlace(listKey) {
      const { error } = await client.rpc('unlink_pilot_place', { p_list_key: listKey });
      if (error) failWrite(error);
    },

    async markPilotNotInFsq(listKey) {
      const { error } = await client.rpc('mark_pilot_not_in_fsq', { p_list_key: listKey });
      if (error) failWrite(error);
    },

    async swapPilotPlace(selectedKey, reserveKey, reason) {
      const parsed = PilotSwapReasonSchema.safeParse(reason);
      if (!parsed.success) throw new DatabaseRuleError('reason_required');
      const { error } = await client.rpc('swap_pilot_place', {
        p_selected_key: selectedKey,
        p_reserve_key: reserveKey,
        p_reason: parsed.data,
      });
      if (error) failWrite(error);
    },
  };
}
